import React, { FC, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { IconButton } from '@margex/ui'

import { THeaderType } from 'components/Header/Header'
import LinkWithLocale from 'components/LinkWithLocale'
import LOCALES from 'constants/locales'

import useLocale from 'hooks/useLocale'

import getLocalesDropdownOptions from 'utils/getLocalesDropdownOptions'

import { DEFAULT_LOCALE, isEmptyHeaderPage } from '@constants'

import arrowRightDropdownItem from 'assets/svg/arrowRightDropdownItem.svg'
import checkIcon from './icons/check_icon.svg'
import globeIcon from './icons/icon-globe.svg'

const LocalesDropdown: FC<ILocalesDropdown> = ({ pageLocales, withAuth }) => {
  const [isActive, setIsActive] = useState(false)
  const { locale, pathname } = useLocale()

  const returnBlogLink = (idx: number): string =>
    LOCALES[idx].code === DEFAULT_LOCALE ? 'https://margex.com/blog' : `https://margex.com/${LOCALES[idx].code}/blog`

  const localesOptions = useMemo(
    () => getLocalesDropdownOptions(pathname, locale, LOCALES, pageLocales),
    [pathname, locale, pageLocales]
  )

  return (
    <DropdownContainer
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      // onClick={() => setIsActive(!isActive)}
    >
      <IconButton>
        <img src={globeIcon} alt={locale} />
      </IconButton>
      <Dropdown $isActive={isActive} $withAuth={Boolean(withAuth)}>
        {localesOptions.map((item, index) => {
          return (
            <DropdownItem
              activeClassName='active-locale'
              key={index}
              to={isEmptyHeaderPage(pathname) ? returnBlogLink(index) : item.url}
              withLocale={false}
              replace>
              {item.name}
            </DropdownItem>
          )
        })}
      </Dropdown>
    </DropdownContainer>
  )
}

export default LocalesDropdown

interface ILocalesDropdown {
  type: THeaderType
  pageLocales?: string[]
  withAuth: boolean | null
}

const DropdownContainer = styled.div`
  position: relative;
  margin: 0.5rem 1rem;
  margin-inline-end: 0;
  margin-inline-start: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  @media ${({ theme }) => theme.device.lg} {
    margin-inline-end: 9px;
    margin-inline-start: 9px;
  }
  @media ${({ theme }) => theme.device.xl} {
    margin-inline-end: 0px;
    margin-inline-start: 0px;
    text-align: right;
  }
`

const Dropdown = styled.div<{ $isActive?: boolean; $withAuth: boolean }>`
  display: ${({ $isActive }) => ($isActive ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  z-index: 10;
  min-width: 10rem;
  padding: 2rem;
  padding-bottom: calc(2rem - 0.8125rem);
  margin: 2rem 0 0;
  font-size: ${({ theme: { fontSize } }) => fontSize.base};
  color: ${({ theme: { colors } }) => colors.default};
  text-align: left;
  list-style: none;
  background: linear-gradient(0deg, #f3f7fd, #f3f7fd), #123482;
  background-clip: padding-box;
  box-shadow: 0px 15px 50px rgba(15, 42, 102, 0.7);
  border: 1px solid ${({ theme: { colors } }) => colors.border};
  border-radius: 0.5rem;
  width: auto;

  transition: opacity 0.4s, visibility 0.4s;

  &::before {
    content: '';
    height: 35px;
    top: -35px;
    left: 0;
    right: 0;
    z-index: -1;
    position: absolute;
  }

  @media ${({ theme }) => theme.device.smMax} {
    left: auto;
    right: 0;

    &::after {
      left: 55%;

      [dir='rtl'] & {
        left: unset;
        right: 55%;
      }
    }

    [dir='rtl'] & {
      right: auto;
      left: 0;
    }
  }

  @media ${props => props.theme.device.lg} and (max-width: 1439px) {
    left: -162%;
  }

  @media ${props => props.theme.device.xxm} and (max-width: 1598px) {
    ${({ $withAuth }) =>
      $withAuth &&
      css`
        left: -200%;
      `}
  }

  & > .active-locale {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 2rem;
      border-radius: 0.25rem;
      background-image: url(${checkIcon});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.brightGreen};
      [dir='rtl'] & {
        left: 0;
        right: unset;
      }
    }
  }
`

const DropdownItem = styled(LinkWithLocale)`
  display: block;
  position: relative;
  width: 210px;
  text-align: start;

  padding: 0.5rem 0.75rem;
  clear: both;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  line-height: 1.375;
  transition: color 0.2s, background-color 0.2s;
  margin-bottom: 0.8125rem;

  &:hover {
    color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};
    text-decoration: none;
    background-color: ${({ theme: { colors } }) => colors.white};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 2rem;
      border-radius: 0.25rem;
      background-image: url(${arrowRightDropdownItem});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};

      [dir='rtl'] & {
        transform: scaleX(-1);
        right: unset;
        left: 0;
      }
    }
  }

  .active-locale {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 0rem;
      margin-inline-start: auto;
      border-radius: 0.25rem;
      background-image: url(${checkIcon});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.brightGreen};
    }
  }
`
